/**
 * NFTMultiSelect - Multi-select grid for choosing NFTs to bundle
 *
 * Allows users to select multiple NFTs from their collection.
 * Shows selection count and provides search/filter functionality.
 */

import React, { useState, useMemo } from 'react';
import { Avatar } from '../../ui/components/Avatar';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { RarityRank } from '../nft/RarityRank';
import { FeatherSearch, FeatherX, FeatherCheck } from '@subframe/core';

export interface SelectableNFT {
  nftTokenId: string;
  name: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  rarityRank?: number;
  totalSupply?: number;
  minSellPrice?: number;
  inBundle?: boolean;
}

interface NFTMultiSelectProps {
  nfts: SelectableNFT[];
  selectedNftIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  maxSelection?: number;
  disabled?: boolean;
  collectionName?: string;
}

export const NFTMultiSelect: React.FC<NFTMultiSelectProps> = ({
  nfts,
  selectedNftIds,
  onSelectionChange,
  maxSelection = 50,
  disabled = false,
  collectionName,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and sort NFTs: exclude those already in bundles, apply search query, sort by rarity rank
  const filteredNfts = useMemo(() => {
    // First, exclude NFTs already in a bundle
    let availableNfts = nfts.filter(nft => !nft.inBundle);

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      availableNfts = availableNfts.filter(
        (nft) =>
          nft.name?.toLowerCase().includes(query) ||
          nft.nftTokenId.toLowerCase().includes(query)
      );
    }

    // Sort by rarity rank (ascending - rarest first), NFTs without rank go to end
    return availableNfts.sort((a, b) => {
      if (a.rarityRank === undefined && b.rarityRank === undefined) return 0;
      if (a.rarityRank === undefined) return 1;
      if (b.rarityRank === undefined) return -1;
      return a.rarityRank - b.rarityRank;
    });
  }, [nfts, searchQuery]);

  // Handle NFT selection toggle
  const handleToggleNFT = (nftTokenId: string) => {
    if (disabled) return;

    const nft = nfts.find((n) => n.nftTokenId === nftTokenId);
    if (nft?.inBundle) return; // Can't select NFTs already in a bundle

    const newSelection = new Set(selectedNftIds);

    if (newSelection.has(nftTokenId)) {
      newSelection.delete(nftTokenId);
    } else {
      // Check max selection limit
      if (newSelection.size >= maxSelection) {
        return;
      }
      newSelection.add(nftTokenId);
    }

    onSelectionChange(newSelection);
  };

  // Select all visible NFTs
  const handleSelectAll = () => {
    if (disabled) return;

    const newSelection = new Set(selectedNftIds);
    let added = 0;

    for (const nft of filteredNfts) {
      if (nft.inBundle) continue;
      if (newSelection.size >= maxSelection) break;
      if (!newSelection.has(nft.nftTokenId)) {
        newSelection.add(nft.nftTokenId);
        added++;
      }
    }

    if (added > 0) {
      onSelectionChange(newSelection);
    }
  };

  // Deselect all
  const handleDeselectAll = () => {
    if (disabled) return;
    onSelectionChange(new Set());
  };

  const selectedCount = selectedNftIds.size;
  const availableCount = filteredNfts.filter((n) => !n.inBundle).length;

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Header with search and selection info */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="text-body text-subtext-color">
            {selectedCount > 0 ? (
              <>
                <span className="text-brand-600 font-medium">{selectedCount}</span>
                {' / '}{maxSelection} selected
              </>
            ) : (
              <>Select NFTs from {collectionName || 'your collection'}</>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedCount > 0 && (
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handleDeselectAll}
                disabled={disabled}
              >
                Clear
              </Button>
            )}
            {selectedCount < maxSelection && availableCount > selectedCount && (
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handleSelectAll}
                disabled={disabled}
              >
                Select All
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <FeatherSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-subtext-color" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-border rounded-md text-body text-default-font bg-default-background focus:outline-none focus:border-brand-600"
            disabled={disabled}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <FeatherX className="h-4 w-4 text-subtext-color hover:text-default-font" />
            </button>
          )}
        </div>
      </div>

      {/* NFT Grid */}
      {filteredNfts.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-body text-subtext-color text-center">
            {searchQuery
              ? 'No NFTs match your search'
              : 'You don\'t own any NFTs from this collection'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto pr-1">
          {filteredNfts.map((nft) => {
            const isSelected = selectedNftIds.has(nft.nftTokenId);
            const isDisabled = disabled;

            return (
              <button
                key={nft.nftTokenId}
                type="button"
                onClick={() => handleToggleNFT(nft.nftTokenId)}
                disabled={isDisabled}
                className={`
                  relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all
                  ${isSelected
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-neutral-border bg-default-background hover:border-brand-300'
                  }
                  ${isDisabled
                    ? 'opacity-70 cursor-not-allowed'
                    : 'cursor-pointer'
                  }
                `}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-600 flex items-center justify-center">
                    <FeatherCheck className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* NFT Image */}
                <Avatar
                  size="large"
                  image={nft.thumbnailUrl || nft.imageUrl}
                >
                  {nft.name?.charAt(0) || '?'}
                </Avatar>

                {/* NFT Name */}
                <span className="text-caption font-medium text-default-font truncate w-full text-center">
                  {nft.name || 'Unnamed NFT'}
                </span>

                {/* Rarity */}
                {nft.rarityRank && nft.totalSupply && (
                  <RarityRank
                    rank={nft.rarityRank}
                    totalSupply={nft.totalSupply}
                    className="text-[10px]"
                  />
                )}

                {/* Listed price indicator */}
                {nft.minSellPrice && nft.minSellPrice > 0 && (
                  <span className="text-[10px] text-warning-600">
                    Listed: {nft.minSellPrice.toLocaleString()} XRP
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Selection summary */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-border">
          <div className="flex -space-x-2">
            {Array.from(selectedNftIds)
              .slice(0, 4)
              .map((nftId) => {
                const nft = nfts.find((n) => n.nftTokenId === nftId);
                return (
                  <Avatar
                    key={nftId}
                    size="x-small"
                    image={nft?.thumbnailUrl || nft?.imageUrl}
                    className="border-2 border-default-background"
                  >
                    {nft?.name?.charAt(0) || '?'}
                  </Avatar>
                );
              })}
            {selectedCount > 4 && (
              <div className="w-6 h-6 rounded-full bg-neutral-100 border-2 border-default-background flex items-center justify-center">
                <span className="text-[10px] text-subtext-color">+{selectedCount - 4}</span>
              </div>
            )}
          </div>
          <span className="text-caption text-subtext-color">
            {selectedCount} NFT{selectedCount > 1 ? 's' : ''} selected for bundle
          </span>
        </div>
      )}
    </div>
  );
};

export default NFTMultiSelect;
